import { useState, useMemo, useRef, Fragment } from 'react';
import SlDialog from '@shoelace-style/shoelace/dist/react/dialog/index.js';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import { useTranslation } from 'react-i18next';
import { Category } from '@anticatrade/shared';

interface CategoryPickerDialogProps {
	isOpen: boolean;
	categories: Category[];
	selectedID: number | undefined;
	onSelect: (id: number) => void;
	onHide: () => void;
}

const CategoryPickerDialog = ({
	isOpen,
	categories,
	selectedID,
	onSelect,
	onHide,
}: CategoryPickerDialogProps) => {
	const [view, setView] = useState<'level1' | 'level2' | 'level3'>('level1');
	const [activeLevel1ID, setActiveLevel1ID] = useState<number | null>(null);
	const [activeLevel2ID, setActiveLevel2ID] = useState<number | null>(null);
	const [searchTerm, setSearchTerm] = useState('');

	const searchInputRef = useRef<any>(null);

	const { t } = useTranslation();

	const { level1, level2Map, level3Map, leafIDs } = useMemo(() => {
		const l1: Category[] = [];
		const l2: Record<number, Category[]> = {};
		const l3: Record<number, Category[]> = {};
		const l1IDs = new Set<number>();
		const l2IDs = new Set<number>();

		for (const c of categories) {
			if (c.parentID == null) {
				l1.push(c);
				l1IDs.add(c.id);
			}
		}

		for (const c of categories) {
			if (c.parentID != null && l1IDs.has(c.parentID)) {
				if (!l2[c.parentID]) {
					l2[c.parentID] = [];
				}
				l2[c.parentID].push(c);
				l2IDs.add(c.id);
			}
		}

		const leaves = new Set<number>();
		for (const c of categories) {
			if (c.parentID != null && l2IDs.has(c.parentID)) {
				if (!l3[c.parentID]) {
					l3[c.parentID] = [];
				}
				l3[c.parentID].push(c);
				leaves.add(c.id);
			}
		}

		// Sort the leaves alphabetically.
		for (const cats of Object.values(l3)) {
			cats.sort((a, b) => a.name.localeCompare(b.name));
		}

		return { level1: l1, level2Map: l2, level3Map: l3, leafIDs: leaves };
	}, [categories]);

	const leafCountByLevel1 = useMemo(() => {
		const counts: Record<number, number> = {};
		for (const l1Cat of level1) {
			let count = 0;
			const l2Cats = level2Map[l1Cat.id] || [];
			for (const l2Cat of l2Cats) {
				count += (level3Map[l2Cat.id] || []).length;
			}
			counts[l1Cat.id] = count;
		}
		return counts;
	}, [level1, level2Map, level3Map]);

	const leafCountByLevel2 = useMemo(() => {
		const counts: Record<number, number> = {};
		for (const l2Cats of Object.values(level2Map)) {
			for (const l2Cat of l2Cats) {
				counts[l2Cat.id] = (level3Map[l2Cat.id] || []).length;
			}
		}
		return counts;
	}, [level2Map, level3Map]);

	const categoryMap = useMemo(() => {
		const map = new Map<number, Category>();
		for (const c of categories) {
			map.set(c.id, c);
		}
		return map;
	}, [categories]);

	const searchResults = useMemo(() => {
		if (searchTerm === '') {
			return [];
		}
		const term = normalize(searchTerm);
		return categories
			.filter((c) => leafIDs.has(c.id) && normalize(c.name).includes(term))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [categories, leafIDs, searchTerm]);

	const getBreadcrumb = (cat: Category) => {
		const parts: string[] = [cat.name];
		let current = cat;
		while (current.parentID != null) {
			const parent = categoryMap.get(current.parentID);
			if (!parent) {
				break;
			}
			parts.unshift(parent.name);
			current = parent;
		}
		return parts.map((part, i) => (
			<Fragment key={i}>
				{i > 0 && ' > '}
				<span className='product__category-picker-item-segment'>{part}</span>
			</Fragment>
		));
	};

	const imageURL = (cat: Category): string | null => {
		if (cat.image == null) {
			return null;
		}
		return `${import.meta.env.VITE_CATEGORY_IMAGE_URL}/${cat.id}/${cat.image.fileName}.webp`;
	};

	const onClickLevel1 = (id: number) => {
		setActiveLevel1ID(id);
		setView('level2');
	};

	const onClickLevel2 = (id: number) => {
		setActiveLevel2ID(id);
		setView('level3');
	};

	const onClickBack = () => {
		if (view === 'level3') {
			setActiveLevel2ID(null);
			setView('level2');
		} else if (view === 'level2') {
			setActiveLevel1ID(null);
			setView('level1');
		}
	};

	const onSearchInput = (e: any) => {
		setSearchTerm(e.target.value);
	};

	const onAfterHide = () => {
		setView('level1');
		setActiveLevel1ID(null);
		setActiveLevel2ID(null);
		setSearchTerm('');
		onHide();
	};

	const isSearching = searchTerm !== '';

	const activeLevel2Cats = activeLevel1ID != null ? level2Map[activeLevel1ID] || [] : [];
	const activeLevel3Cats = activeLevel2ID != null ? level3Map[activeLevel2ID] || [] : [];

	const renderLevel1 = () => (
		<div className='product__category-picker-groups'>
			{level1.map((cat) => {
				const src = imageURL(cat);
				return (
					<button
						key={cat.id}
						className='product__category-picker-group'
						onClick={() => onClickLevel1(cat.id)}
					>
						{src && (
							<img
								className='product__category-picker-group-image'
								src={src}
								alt={cat.name}
							/>
						)}
						<div className='product__category-picker-group-info'>
							<div>{cat.name}</div>
							<div className='product__category-picker-group-count'>
								{leafCountByLevel1[cat.id] || 0}
							</div>
						</div>
					</button>
				);
			})}
		</div>
	);

	const renderLevel2 = () => (
		<>
			<button className='product__category-picker-back' onClick={onClickBack}>
				<SlIcon name='arrow-left' />
				{t('Back')}
			</button>
			<div className='product__category-picker-groups'>
				{activeLevel2Cats.map((cat) => {
					const src = imageURL(cat);
					return (
						<button
							key={cat.id}
							className='product__category-picker-group'
							onClick={() => onClickLevel2(cat.id)}
						>
							{src && (
								<img
									className='product__category-picker-group-image'
									src={src}
									alt={cat.name}
								/>
							)}
							<div className='product__category-picker-group-info'>
								<div>{cat.name}</div>
								<div className='product__category-picker-group-count'>
									{leafCountByLevel2[cat.id] || 0}
								</div>
							</div>
						</button>
					);
				})}
			</div>
		</>
	);

	const renderLevel3 = () => (
		<>
			<button className='product__category-picker-back' onClick={onClickBack}>
				<SlIcon name='arrow-left' />
				{t('Back')}
			</button>
			<div className='product__category-picker-items'>
				{activeLevel3Cats.map((cat) => (
					<button
						key={cat.id}
						className='product__category-picker-item'
						onClick={() => onSelect(cat.id)}
					>
						{cat.id === selectedID && (
							<SlIcon className='product__picker-check' name='check-lg' />
						)}
						{cat.name}
					</button>
				))}
			</div>
		</>
	);

	const renderSearchResults = () => (
		<div className='product__category-picker-items'>
			{searchResults.length === 0 ? (
				<div className='product__category-picker-empty'>{t('No results found')}</div>
			) : (
				searchResults.map((cat) => (
					<button
						key={cat.id}
						className='product__category-picker-item'
						onClick={() => onSelect(cat.id)}
					>
						{cat.id === selectedID && (
							<SlIcon className='product__picker-check' name='check-lg' />
						)}
						<span className='product__category-picker-item-breadcrumb'>
							{getBreadcrumb(cat)}
						</span>
					</button>
				))
			)}
		</div>
	);

	return (
		<SlDialog
			className='product__category-picker-dialog'
			label={t('Category')}
			open={isOpen}
			onSlAfterHide={onAfterHide}
			onSlAfterShow={() => {
				if (window.innerWidth > 900) searchInputRef.current?.focus();
			}}
		>
			<SlInput
				ref={searchInputRef}
				className='product__category-picker-search'
				placeholder={t('Search categories...')}
				onSlInput={onSearchInput}
				value={searchTerm}
				size='small'
				clearable
			>
				<SlIcon name='x-circle' slot='clear-icon' />
			</SlInput>
			<div className='product__category-picker-body'>
				{isSearching
					? renderSearchResults()
					: view === 'level1'
						? renderLevel1()
						: view === 'level2'
							? renderLevel2()
							: renderLevel3()}
			</div>
		</SlDialog>
	);
};

const normalize = (s: string) =>
	s
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();

export { CategoryPickerDialog };
